import React from 'react';

import Pagination from '@components/Pagination';
import Spinner from '@components/Spinner';
import { useSearchResults, useSearchState } from '@hooks';
import useSearchEntities from '@hooks/useSearchEntities';
import { search_page_path } from '@routes';
import { Scope } from '@utils/backend-api/search';
import { PaginationType } from '@utils/backend-api/subscriber_directory';

import SearchArticlesList from './ArticlesList';
import DirectorySearchResults from './DirectorySearchResults';
import SearchHeader from './Header';
import SearchPostsList from './PostsList';
import { SearchPageProps } from './index';

const SearchContent = ({
  page: initialPage,
  pageCount: initialPageCount,
  query: initialQuery,
  scope: initialScope,
  sort: initialSort,
}: SearchPageProps) => {
  const {
    handleClearSearch,
    handlePageChange,
    handleScopeChange,
    handleSearchSubmit,
    handleSortChange,
    page,
    query,
    scope,
    setQuery,
    sort,
  } = useSearchState({ initialQuery, initialPage, initialSort, initialScope });

  const { articleData, forumData, directoryData, isLoading, isFetching } = useSearchResults({
    page,
    query,
    scope,
    sort,
  });
  const { data: entitiesData } = useSearchEntities({ query });

  const totalPages =
    scope === Scope.Articles
      ? articleData?.pageCount ?? initialPageCount ?? 1
      : forumData?.totalPages ?? 1;

  const paginationData: PaginationType = {
    page,
    totalPages,
    hasNextPage: page < totalPages,
    nextPage: page + 1,
  };

  const renderResults = () => {
    switch (scope) {
      case Scope.Articles:
        return <SearchArticlesList content={articleData?.content} />;
      case Scope.Forum:
        return <SearchPostsList content={forumData?.forum_posts} />;
      case Scope.Directory:
        return <DirectorySearchResults data={directoryData} />;
      default:
        return null;
    }
  };

  return (
    <div className="mx-12">
      <SearchHeader
        onClearSearch={handleClearSearch}
        onScopeChange={handleScopeChange}
        onSearchSubmit={handleSearchSubmit}
        onSortChange={handleSortChange}
        scope={scope}
        searchQuery={query}
        setSearchQuery={setQuery}
        sort={sort}
        topRelatedEntities={entitiesData?.topRelatedEntities}
        totalResults={articleData?.totalResults}
      />
      {isLoading || isFetching ? (
        <div className="mt-28 flex justify-center py-40">
          <Spinner marginY="large" />
        </div>
      ) : (
        <>
          <div className="relative mt-28">{renderResults()}</div>
          {totalPages > 1 && (
            <Pagination
              getPath={(page) => search_page_path({ query, scope, page, sort })}
              paginationData={paginationData}
              setPage={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SearchContent;
